import { useNavigate, useRouterState } from '@tanstack/react-router';
import { useEffect, useRef, type ReactNode } from 'react';

import { useSession } from '@platform/api-client/react';
import { Alert, Card, CardBody, PageHeader, Spinner } from '@platform/ui';

import { loginPathFor } from './next-path';
import { usePermissions } from './permissions';

/**
 * The route guards (M2.8, rules §17.2, §22.2, §22.5).
 *
 * Both of these are **rendering** guards and neither is an authorization one. Every
 * endpoint behind them enforces its own access on the server; what they do is stop an
 * operator being shown an empty console and a stack of failed requests.
 *
 * `MFA_ENROLMENT_PATHS` is the list a session with no second factor may still reach.
 * It matches the server's `@AllowWithoutMfa()` set (SECURITY.md §4.3): the enrolment
 * screen itself, the login screen, and nothing else - because a staff or platform
 * session without MFA answers `403 MFA_REQUIRED` on every other route, and rendering a
 * dashboard that cannot load is worse than rendering the one screen that can.
 */

/** Paths reachable before MFA enrolment is complete. */
export const MFA_ENROLMENT_PATHS = ['/login', '/mfa/enroll'];

/**
 * M13.7. Every guarded route keeps its `h1` in the two states where the screen itself
 * never renders: a screen with no level-1 heading has no name a reader can navigate to,
 * and `/settings/domains` and `/feedback` were reaching axe in exactly that state
 * (WCAG 1.3.1, 2.4.6). The heading is the guard's own, because the guard is what is on
 * screen.
 */
function Pending({ label }: { label: string }) {
  return (
    /* `role="status"` on the wrapper, and the spinner silent: the heading IS the status
       message, so announcing it twice — once as an `h1`, once as the spinner's `sr-only`
       label — would be the same sentence read out to nobody's benefit. */
    <div role="status">
      <PageHeader title={label} />
      <Card className="p-6">
        <Spinner label={null} />
      </Card>
    </div>
  );
}

export interface RequireAuthProps {
  children: ReactNode;
}

/**
 * Four states, all deliberate:
 *
 * - **pending** - the boot call is in flight. A spinner, because unlike the patient
 *   funnel this console has a full shell behind it and a blank main area reads as broken.
 * - **anonymous** - redirect to `/login?next=…`, replacing the history entry so Back
 *   does not bounce between the guard and the login screen. Latched, because the guard
 *   is still mounted while the router transitions.
 * - **error** - the session call itself failed. An alert, **not** a redirect: sending
 *   someone to a login screen that also cannot reach the API is a loop.
 * - **signed in but not an Object Labs account** - says so. The API refuses this session
 *   on every platform route anyway; what this avoids is a console full of 403s and no
 *   explanation.
 */
export function RequireAuth({ children }: RequireAuthProps) {
  const navigate = useNavigate();
  const session = useSession();
  const location = useRouterState({ select: (state) => state.location });

  const authenticated = session.data?.authenticated === true;
  const anonymous = session.isSuccess && !authenticated;

  const redirected = useRef(false);
  useEffect(() => {
    if (!anonymous || redirected.current) return;
    redirected.current = true;
    void navigate({
      to: loginPathFor(location.pathname, location.searchStr),
      replace: true,
    });
  }, [anonymous, navigate, location.pathname, location.searchStr]);

  if (session.isPending) return <Pending label="Checking your sign-in" />;

  if (session.isError) {
    return (
      <Alert tone="error" title="We could not check your sign-in.">
        {session.error.message} Please refresh the page.
      </Alert>
    );
  }

  // The redirect above is already scheduled; render nothing rather than a flash of the
  // protected screen.
  if (!authenticated) return null;

  if (session.data.user?.population !== 'platform') {
    return (
      <Alert tone="error" title="This console is for Object Labs staff.">
        Your account signs in to a clinic&rsquo;s own admin app, not to the HeyAllergy console.
      </Alert>
    );
  }

  return <>{children}</>;
}

export interface RequireMfaProps {
  children: ReactNode;
}

/**
 * The enrolment gate (SECURITY.md §4.3, FR-SUPER-01).
 *
 * "Super admins cannot reach any tenant data until MFA enrollment completes" is a server
 * guard; this is the screen that makes the requirement actionable instead of a wall of
 * `403 MFA_REQUIRED`. Sign-out stays reachable throughout, because an operator who
 * cannot enrol must still be able to leave.
 */
export function RequireMfa({ children }: RequireMfaProps) {
  const navigate = useNavigate();
  const session = useSession();
  const pathname = useRouterState({ select: (state) => state.location.pathname });

  const mfa = session.data?.mfa;
  const mustEnroll = mfa?.required === true && mfa.enrolled !== true;
  const exempt = MFA_ENROLMENT_PATHS.includes(pathname);

  const redirected = useRef(false);
  useEffect(() => {
    if (!mustEnroll || exempt || redirected.current) return;
    redirected.current = true;
    void navigate({ to: '/mfa/enroll', replace: true });
  }, [mustEnroll, exempt, navigate]);

  if (mustEnroll && !exempt) return null;
  return <>{children}</>;
}

export interface RequirePermissionProps {
  /** Every key must be held. Empty means "any signed-in operator". */
  keys: readonly string[];
  children: ReactNode;
  /** Shown instead of the default refusal, e.g. a read-only version of the screen. */
  fallback?: ReactNode;
}

/**
 * Hides a screen the operator's role cannot use.
 *
 * It refuses while the session is still loading (rules §22.5: access defaults to denied
 * when the permission cannot be determined) by showing the pending state rather than the
 * screen - never by showing the screen and hoping.
 */
export function RequirePermission({ keys, children, fallback }: RequirePermissionProps) {
  const { canAll, isPending } = usePermissions();

  if (isPending) return <Pending label="Checking your access" />;
  if (canAll(keys)) return <>{children}</>;
  if (fallback !== undefined) return <>{fallback}</>;

  return (
    <>
      {/* M13.7: the refusal is a page in its own right, so it carries the page's h1. */}
      <PageHeader title="No access" />
      <Card>
        <CardBody>
          <Alert tone="warning" title="You do not have access to this.">
            Your platform role does not include this area. Ask a platform administrator if you need
            it.
          </Alert>
        </CardBody>
      </Card>
    </>
  );
}
